import heic2any from 'heic2any';
import i18n from '../../i18n/config';

const SUPPORTED_IMAGE_TYPES = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
];

const HEIC_EXTENSIONS = ['.heic', '.heif'];

export const isHeicFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    return fileType === 'image/heic' || 
           fileType === 'image/heif' || 
           HEIC_EXTENSIONS.some(ext => fileName.endsWith(ext));
};

export const isSupportedImageType = (file: File): boolean => {
    return SUPPORTED_IMAGE_TYPES.includes(file.type.toLowerCase());
};

export const convertHeicToJpeg = async (file: File): Promise<File> => {
    try {
        const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8
        });
        
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        
        return new File([blob], newFileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
        });
    } catch (error) {
        if (error && typeof error === 'object' && 'message' in error
            && typeof (error as {message: unknown}).message === 'string'
            && (error as {message: string}).message.includes('already browser readable')) {
            return file;
        }
        throw new Error(i18n.t('errors.image_heic'));
    }
};

export const processImageFile = async (file: File): Promise<{ file: File; converted: boolean }> => {
    if (isHeicFile(file)) {
        const convertedFile = await convertHeicToJpeg(file);
        return { file: convertedFile, converted: true };
    }
    
    if (!isSupportedImageType(file)) {
        throw new Error(i18n.t('errors.image_format'));
    }
    
    return { file, converted: false };
};

export const validateImageSize = (file: File, maxSizeInMB: number = 10): boolean => {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
};

export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.width, height: img.height });
        };
        
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error(i18n.t('errors.image_dimensions')));
        };
        
        img.src = url;
    });
};
